'use client'

import { useState, useMemo } from 'react'
import { cn } from '@paitox/ui/lib/utils'
import {
  BarChart3, TrendingUp, Users, Calendar, Activity,
  FileSpreadsheet, Sparkles, AlertCircle, CheckCircle, Lightbulb
} from 'lucide-react'
import { db } from '../lib/db'

export function ReportsPage() {
  const [activeTab, setActiveTab] = useState<'kpis' | 'suggestions'>('kpis')

  const payments = useMemo(() => db.getPayments(), [])
  const appointments = useMemo(() => db.getAppointments(), [])
  const clients = useMemo(() => db.getClients(), [])
  const services = useMemo(() => db.getServices(), [])

  const totalRevenue = useMemo(() => {
    return payments.filter(p => p.status === 'PAID').reduce((sum, p) => sum + p.amount, 0)
  }, [payments])

  const ticketMedio = useMemo(() => {
    const paidAppts = payments.filter(p => p.type === 'appointment' && p.status === 'PAID')
    if (paidAppts.length === 0) return 0
    return Math.round(paidAppts.reduce((sum, p) => sum + p.amount, 0) / paidAppts.length)
  }, [payments])

  const servicePopularity = useMemo(() => {
    const popularity: Record<string, number> = {}
    appointments.forEach(a => {
      popularity[a.service] = (popularity[a.service] || 0) + 1
    })
    return Object.entries(popularity)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 4)
  }, [appointments])

  const clientSegments = useMemo(() => {
    const segments = { vip: 0, frecuente: 0, nueva: 0, otros: 0 }
    clients.forEach(c => {
      if (c.tags.includes('VIP')) segments.vip++
      else if (c.tags.includes('Frecuente')) segments.frecuente++
      else if (c.tags.includes('Nueva')) segments.nueva++
      else segments.otros++
    })
    return segments
  }, [clients])

  const fmtCLP = (n: number) => `$${n.toLocaleString('es-CL')}`

  // The 20 high-value suggestions to select / choose
  const suggestions = [
    { id: 1, title: 'Recordatorio por WhatsApp Directo con Plantillas Autocompletables', desc: 'Permite al personal enviar un recordatorio semi-automático en un clic abriendo la app de WhatsApp con datos precargados.', category: 'Marketing' },
    { id: 2, title: 'Análisis de Clientes ABC automático', desc: 'Segmentación de clientes en base a su frecuencia y volumen de gasto para premiar la fidelización de clientes VIP clase A.', category: 'Fidelización' },
    { id: 3, title: 'Notificación de Disponibilidad en Lista de Espera', desc: 'Cuando una cita se cancela, el sistema avisa automáticamente por correo/push a los primeros en lista de espera.', category: 'Operaciones' },
    { id: 4, title: 'Sincronización Bidireccional de Calendarios', desc: 'Integración avanzada con Google Calendar para evitar sobre-reservas y bloqueos de agenda en tiempo real.', category: 'Sistemas' },
    { id: 5, title: 'Módulo de Diagnóstico Estético Inicial / Ficha Médica', desc: 'Formulario con consentimiento informado, alergias, tipo de piel (Fitzpatrick), antecedentes clínicos y firmas digitales.', category: 'Médico' },
    { id: 6, title: 'Generador de Cupones QR Dinámicos para Regalos', desc: 'Gift Cards con códigos QR que el cliente puede portar en Apple Wallet o presentar físicamente para escaneo veloz.', category: 'Ventas' },
    { id: 7, title: 'Comisión Dinámica para Especialistas', desc: 'Asignación de comisiones fijas o por porcentaje de servicio realizado para incentivar al staff del centro.', category: 'Personal' },
    { id: 8, title: 'Portal de Auto-Agendamiento Público del Cliente', desc: 'Mini-sitio web donde los clientes eligen categoría, especialista, fecha y hora libre sin necesidad de llamar.', category: 'Ventas' },
    { id: 9, title: 'Pre-pago de Señal (Garantía de Cita)', desc: 'Pasarela de pago para exigir un abono del 20% al reservar en línea, reduciendo la tasa de inasistencia (No-Show).', category: 'Finanzas' },
    { id: 10, title: 'Cierre de Caja Diario Automatizado', desc: 'Reporte automatizado al final de la jornada sumando caja chica, transacciones POS y transferencias bancarias conciliadas.', category: 'Finanzas' },
    { id: 11, title: 'Historial de Fotos de Progreso por Sesión', desc: 'Almacenar fotos de "Antes y Después" encriptadas directamente en la ficha del cliente para evidenciar resultados.', category: 'Operaciones' },
    { id: 12, title: 'Notificaciones Push PWA para Recordatorios Cortos', desc: 'Envío de notificaciones directas al móvil del recepcionista o cliente mediante Service Workers sin costo de SMS.', category: 'Sistemas' },
    { id: 13, title: 'Encuesta Automática de Calidad (NPS)', desc: 'Envío de link de satisfacción de 1 a 10 a las 2 horas de finalizar el tratamiento con redirección a Google Reviews.', category: 'Marketing' },
    { id: 14, title: 'Control de Inventario de Insumos / Stock Crítico', desc: 'Descuento automático de ampollas, cremas o insumos por cada servicio realizado con alertas de stock bajo.', category: 'Logística' },
    { id: 15, title: 'Firma Digital de Consentimientos en Tablet', desc: 'Los clientes firman las políticas de tratamiento con el dedo directamente en la pantalla de recepción antes de pasar.', category: 'Operaciones' },
    { id: 16, title: 'Módulo de Membresías Mensuales Estilo Club', desc: 'Cobro recurrente mensual para dar acceso ilimitado a un buffet de servicios seleccionados (ej: uñas o masajes).', category: 'Ventas' },
    { id: 17, title: 'Alertas de Cumpleaños con Cupones de Descuento', desc: 'Automatización que saluda al cumpleañero y le ofrece un 15% de descuento en tratamientos faciales durante su mes.', category: 'Marketing' },
    { id: 18, title: 'Tratamientos Multi-Recurso y Bloqueo de Salas', desc: 'Configuración avanzada donde un servicio requiere múltiples recursos (ej: cabina doble, camilla especial, especialista senior).', category: 'Operaciones' },
    { id: 19, title: 'Boleta Electrónica SII Integrada (LibreDTE)', desc: 'Emisión automática de boletas exentas o afectas al presionar "Completar Cita" para cumplimiento normativo en Chile.', category: 'Sistemas' },
    { id: 20, title: 'Seguimiento de Clientes Inactivos (Re-engagement)', desc: 'Listado inteligente de clientes que no han agendado en los últimos 60 días para campañas focalizadas.', category: 'Fidelización' }
  ]

  return (
    <div className="flex flex-col h-full overflow-hidden bg-[hsl(220,18%,96%)] p-4 gap-4">
      {/* Tab Switcher */}
      <div className="flex items-center justify-between gap-3 shrink-0">
        <div>
          <h1 className="text-lg font-bold text-slate-800">Reportes e Ideas de Optimización</h1>
          <p className="text-xs text-slate-500">Métricas clave del centro de estética e ideas de mejora</p>
        </div>
        <div className="flex bg-slate-200 rounded-lg p-0.5 gap-0.5">
          <button onClick={() => setActiveTab('kpis')}
            className={cn("px-4 py-1.5 text-xs font-semibold rounded-md transition-all flex items-center gap-1.5", activeTab === 'kpis' ? "bg-white text-cyan-700 shadow" : "text-slate-500 hover:text-slate-700")}>
            <BarChart3 className="h-3.5 w-3.5" /> KPIs Negocio
          </button>
          <button onClick={() => setActiveTab('suggestions')}
            className={cn("px-4 py-1.5 text-xs font-semibold rounded-md transition-all flex items-center gap-1.5", activeTab === 'suggestions' ? "bg-white text-cyan-700 shadow" : "text-slate-500 hover:text-slate-700")}>
            <Lightbulb className="h-3.5 w-3.5" /> 20 Sugerencias (Elegir)
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        {activeTab === 'kpis' ? (
          <div className="space-y-4">
            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              {[
                { label: 'Ventas Totales', value: fmtCLP(totalRevenue), sub: 'Facturación bruta del centro', icon: TrendingUp, color: 'text-emerald-600 bg-emerald-50' },
                { label: 'Ticket Promedio', value: fmtCLP(ticketMedio), sub: 'Gasto promedio por cita', icon: BarChart3, color: 'text-cyan-600 bg-cyan-50' },
                { label: 'Clientes Únicos', value: `${clients.length}`, sub: 'Registrados en base de datos', icon: Users, color: 'text-violet-600 bg-violet-50' },
                { label: 'Citas Totales', value: `${appointments.length}`, sub: 'Historial agendamiento', icon: Calendar, color: 'text-blue-600 bg-blue-50' }
              ].map(kpi => (
                <div key={kpi.label} className="bg-white rounded-2xl border border-slate-200/70 p-4 shadow-sm flex items-center gap-3">
                  <div className={cn("h-10 w-10 rounded-xl flex items-center justify-center shrink-0", kpi.color)}>
                    <kpi.icon className="h-5 w-5" />
                  </div>
                  <div>
                    <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider block">{kpi.label}</span>
                    <span className="text-base font-extrabold text-slate-800 mt-0.5 block">{kpi.value}</span>
                    <span className="text-[10px] text-slate-400 mt-0.5 block">{kpi.sub}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Visual breakdown mock sections */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white rounded-2xl border border-slate-200/70 p-5 shadow-sm">
                <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-4">Tratamientos Más Populares</h3>
                <div className="space-y-3">
                  {servicePopularity.map(([name, count]) => (
                    <div key={name} className="flex items-center justify-between">
                      <span className="text-xs text-slate-600 font-medium">{name}</span>
                      <div className="flex items-center gap-3 w-1/2">
                        <div className="h-2 bg-slate-100 rounded-full flex-1 overflow-hidden">
                          <div className="h-full bg-cyan-500 rounded-full" style={{ width: `${(count / appointments.length) * 100}%` }} />
                        </div>
                        <span className="text-xs font-bold text-slate-800 min-w-8 text-right">{count} citas</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-slate-200/70 p-5 shadow-sm">
                <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-4">Segmentación ABC de Clientes</h3>
                <div className="space-y-3">
                  {[
                    { label: 'VIP (Clase A - Fidelizado)', count: clientSegments.vip, color: 'bg-amber-400' },
                    { label: 'Frecuentes (Clase B - Recurrente)', count: clientSegments.frecuente, color: 'bg-blue-400' },
                    { label: 'Nuevos (Clase C - Captación)', count: clientSegments.nueva, color: 'bg-emerald-400' },
                    { label: 'Otros (Sin clasificar)', count: clientSegments.otros, color: 'bg-slate-300' }
                  ].map(seg => (
                    <div key={seg.label} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className={cn("h-2.5 w-2.5 rounded-full", seg.color)} />
                        <span className="text-xs text-slate-600 font-medium">{seg.label}</span>
                      </div>
                      <span className="text-xs font-bold text-slate-800">{seg.count} clientes</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-cyan-50 border border-cyan-200 rounded-2xl p-4 flex gap-3 text-cyan-800">
              <Lightbulb className="h-5 w-5 text-cyan-600 shrink-0 mt-0.5" />
              <div>
                <h4 className="font-bold text-sm">Sugerencias Estratégicas para el Sistema Paitox</h4>
                <p className="text-xs text-cyan-700 mt-1 leading-relaxed">
                  He analizado minuciosamente tu sistema de estética/spa. A continuación, te ofrezco exactamente **20 propuestas de alta gama** clasificadas para que elijas las que mejor se adapten a la fase actual de tu negocio.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {suggestions.map(s => (
                <div key={s.id} className="bg-white rounded-2xl border border-slate-200/70 p-5 shadow-sm flex flex-col justify-between hover:border-cyan-300 transition-all group">
                  <div>
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">
                        {s.category}
                      </span>
                      <span className="text-xs text-cyan-600 font-bold">#{s.id}</span>
                    </div>
                    <h4 className="font-bold text-sm text-slate-800 group-hover:text-cyan-600 transition-colors">{s.title}</h4>
                    <p className="text-xs text-slate-500 mt-2 leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
